import { Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { ModalExit } from "./enums";
import { ConfirmComponent } from "../modals/confirm/confirm.component";
import { ChoiceComponent } from "../modals/choice/choice.component";
import { InputComponent } from "../modals/input/input.component";

@Injectable()
export class ModalHandler {
  dialogRef: MatDialogRef<any>[] = [];

  constructor(private dialog: MatDialog) {}

  closeAllModals() {
    this.dialogRef.forEach((dialog) => {
      dialog.close();
    });
  }

  fullscreenModal(component: any, dialogConfig: MatDialogConfig | any, returnOutput?: boolean): Observable<any> {
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = "fullscreen-dialog";
    dialogConfig.height = "90vh";
    dialogConfig.width = "99vw";
    dialogConfig.maxWidth = "99vw";
    dialogConfig.maxHeight = "90vh";
    return this.openModal(component, dialogConfig, returnOutput);
  }

  confirm(body: string, header?: string, showCancel = true, cancelText = null, acceptText = null): Promise<boolean> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: header ? header : "Are you sure?",
      message: body,
      showCancel: showCancel,
    };
    if (cancelText) dialogConfig.data.cancelText = cancelText;
    if (acceptText) dialogConfig.data.acceptText = acceptText;
    dialogConfig.autoFocus = "dialog";
    dialogConfig.width = "40vw";
    dialogConfig.disableClose = true;
    dialogConfig.backdropClass = "blur-backdrop";
    return new Promise<boolean>((resolve) => {
      const dialogRef = this.dialog.open(ConfirmComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((result: ModalExit) => {
        const isConfirmed = result == ModalExit.COMPLETE;
        resolve(isConfirmed);
      });
    });
  }

  choice<T>(body: string, options: T[], canClose = true): Promise<T> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: body,
      options: options,
      canClose: canClose,
    };
    dialogConfig.autoFocus = "dialog";
    dialogConfig.width = "40vw";
    dialogConfig.disableClose = true;
    dialogConfig.backdropClass = "blur-backdrop";
    return new Promise<T>((resolve) => {
      const dialogRef = this.dialog.open(ChoiceComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((result) => {
        return resolve(result);
      });
    });
  }

  radioChoice<T>(body: string, options: T[], canClose = true): Promise<T> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: body,
      options: options,
      canClose: canClose,
      radio: true,
    };
    dialogConfig.autoFocus = "dialog";
    dialogConfig.width = "40vw";
    dialogConfig.disableClose = true;
    dialogConfig.backdropClass = "blur-backdrop";
    return new Promise<T>((resolve) => {
      const dialogRef = this.dialog.open(ChoiceComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((result) => {
        return resolve(result);
      });
    });
  }

  input(message: string, label: string, helpText: string = null, type = "string"): Promise<string | number> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      message: message,
      label: label,
      helpText: helpText,
      type: type,
    };
    dialogConfig.autoFocus = "dialog";
    dialogConfig.width = "40vw";
    dialogConfig.disableClose = true;
    dialogConfig.backdropClass = "blur-backdrop";
    return new Promise<string | number>((resolve) => {
      const dialogRef = this.dialog.open(InputComponent, dialogConfig);

      dialogRef.afterClosed().subscribe((result) => {
        return resolve(result);
      });
    });
  }

  alert(header: string, body?: string): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: header,
      message: body ? body : "This can take some time. You may close the window and monitor from the events side panel",
      showCancel: false,
      acceptText: "OK",
    };
    dialogConfig.autoFocus = "dialog";
    dialogConfig.width = "40vw";
    dialogConfig.disableClose = true;
    dialogConfig.backdropClass = "blur-backdrop";
    this.dialog.open(ConfirmComponent, dialogConfig);
  }

  alertWait(header: string, body?: string): Promise<void> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: header,
      message: body,
      showCancel: false,
      acceptText: "OK",
    };
    dialogConfig.autoFocus = "dialog";
    dialogConfig.width = "40vw";
    dialogConfig.disableClose = true;
    dialogConfig.backdropClass = "blur-backdrop";
    return new Promise<void>((resolve) => {
      const dialogRef = this.dialog.open(ConfirmComponent, dialogConfig);

      dialogRef.afterClosed().subscribe(() => {
        return resolve();
      });
    });
  }

  throwError(title: string, body: string): Observable<ModalExit> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.maxWidth = "30vw";
    dialogConfig.data = {
      title: title,
      message: body,
      showCancel: false,
      acceptText: "OK",
    };
    dialogConfig.disableClose = true; // This is similar to ignoreBackdropClick: true
    return this.openModal(ConfirmComponent, dialogConfig, false);
  }

  openModal(component: any, dialogConfig: MatDialogConfig | any, returnOutput: true, noBackdrop: false): Observable<any>;
  openModal(
    component: any,
    dialogConfig: MatDialogConfig | any,
    returnOutput?: false | undefined,
    noBackdrop?: false | undefined,
  ): Observable<ModalExit>;
  openModal(
    component: any,
    dialogConfig: MatDialogConfig | any,
    returnOutput?: boolean,
    noBackdrop?: boolean,
  ): Observable<any> | Observable<ModalExit>;
  openModal(
    component: any,
    dialogConfig: MatDialogConfig | any,
    returnOutput?: boolean,
    noBackdrop?: boolean,
  ): Observable<any> | Observable<ModalExit> {
    if (noBackdrop) {
      dialogConfig.backdropClass = "transparent-backdrop";
      dialogConfig.hasBackdrop = false;
    } else {
      dialogConfig.backdropClass = "blur-backdrop";
      dialogConfig.hasBackdrop = true;
    }
    if (!dialogConfig.maxWidth) dialogConfig.maxWidth = "90vw";
    dialogConfig.maxHeight = "90vh";
    dialogConfig.autoFocus = "dialog";
    dialogConfig.disableClose = true;
    const dialogRef = this.dialog.open(component, dialogConfig);
    this.dialogRef.push(dialogRef);

    if (returnOutput) {
      return new Observable<any>((observer) => {
        const subscription = dialogRef.afterClosed().subscribe((output) => {
          observer.next(output);
          observer.complete();
          this.dialogRef = this.dialogRef.filter((ref) => ref !== dialogRef);
          subscription.unsubscribe();
        });

        return {
          unsubscribe() {
            subscription.unsubscribe();
          },
        };
      });
    }

    return new Observable<ModalExit>((observer) => {
      const subscription = dialogRef.afterClosed().subscribe((exitCode) => {
        observer.next(exitCode);
        observer.complete();
        this.dialogRef = this.dialogRef.filter((ref) => ref !== dialogRef);
        subscription.unsubscribe();
      });

      return {
        unsubscribe() {
          subscription.unsubscribe();
        },
      };
    });
  }
}
